import { useState, useEffect } from "react";
import "@/App.css";
import { 
  Phone, 
  MessageCircle, 
  MapPin, 
  Clock, 
  Menu, 
  X, 
  ChevronRight,
  Activity,
  Heart,
  Facebook,
  Navigation
} from "lucide-react";
import { Button } from "./components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";

// Assets
const LOGO = "https://customer-assets.emergentagent.com/job_muv-therapy/artifacts/69xppb6r_Untitled_design.png";
const HERO_BG = "https://images.unsplash.com/photo-1649751295468-953038600bef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxwaHlzaW90aGVyYXB5JTIwbWFzc2FnZSUyMHRoZXJhcHklMjB0cmVhdG1lbnR8ZW58MHx8fHwxNzczODY5MDQ2fDA&ixlib=rb-4.1.0&q=85";
const MASSAGE_IMG = "https://images.unsplash.com/photo-1542848284-8afa78a08ccb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxwaHlzaW90aGVyYXB5JTIwbWFzc2FnZSUyMHRoZXJhcHklMjB0cmVhdG1lbnR8ZW58MHx8fHwxNzczODY5MDQ2fDA&ixlib=rb-4.1.0&q=85";
const REHAB_IMG = "https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjByZWhhYmlsaXRhdGlvbiUyMHBoeXNpY2FsJTIwdGhlcmFweSUyMHN0cmV0Y2hpbmd8ZW58MHx8fHwxNzczODY5MDQ3fDA&ixlib=rb-4.1.0&q=85";

// Contact Info
const CONTACT = {
  phone: "(579) 723-9601",
  phoneClean: "5797239601",
  messenger: "https://m.me/cliniqueMuV",
  facebook: "https://www.facebook.com/cliniqueMuV",
  address: "1887 Ch. du Tremblay, Local 001, Longueuil, QC J4N 1A4",
  addressEncoded: "1887+Ch+du+Tremblay,+Longueuil,+QC+J4N+1A4",
  hours: {
    weekdays: "Lun-Ven: 9h00 - 19h00",
    weekend: "Sam-Dim: 9h00 - 17h00"
  },
  coordinates: { lat: 45.550540, lng: -73.461677 }
};

// Navigation links
const NAV_LINKS = [
  { id: "accueil", label: "Accueil" },
  { id: "services", label: "Services" },
  { id: "apropos", label: "À propos" },
  { id: "contact", label: "Contact" }
];

// Services data
const SERVICES = [
  {
    id: "massotherapie",
    title: "Massothérapie et Kinésithérapie",
    description: "Bien être global, soulagement des tensions musculaires, réduction du stress. Supplémentation et soins personnalisés.",
    icon: Heart,
    image: MASSAGE_IMG,
    benefits: ["Soins de détente et thérapeutique", "Amélioration du sommeil", "Diminution du stress", "Récupération musculaire"]
  },
  {
    id: "readaptation",
    title: "Réadaptation Sportive",
    description: "Retour au jeu en toute sécurité après une blessure. Programme personnalisé pour athlètes de tous niveaux visant la performance optimale.",
    icon: Activity,
    image: REHAB_IMG,
    benefits: ["Prévention des blessures", "Amélioration de la performance", "Retour au sport", "Renforcement ciblé"]
  }
];

// Navigation Component
const NavBar = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`nav-fixed ${isScrolled ? "nav-scrolled" : ""}`} data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#accueil" 
            onClick={(e) => { e.preventDefault(); scrollToSection("accueil"); }}
            className="logo-hover"
            data-testid="logo-link"
          >
            <img 
              src={LOGO} 
              alt="Clinique Mūv" 
              className="h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-sm font-bold uppercase tracking-widest text-gray-300 hover:text-[#E8692D] transition-colors duration-300"
                data-testid={`nav-link-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#464C44] border-t border-white/10" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left py-3 px-4 text-white font-semibold hover:bg-white/5 rounded-lg transition-colors"
                data-testid={`mobile-nav-link-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onOpenBooking }) => {
  return (
    <section 
      id="accueil" 
      className="hero-section"
      style={{ backgroundImage: `url(${HERO_BG})` }}
      data-testid="hero-section"
    >
      <div className="hero-overlay" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          <p className="text-[#E8692D] text-sm font-bold uppercase tracking-widest mb-4 animate-fade-in-up">
            Clinique de thérapie physique
          </p>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight uppercase text-white mb-6 animate-fade-in-up animate-delay-100">
            Retrouver votre <span className="text-[#E8692D]">mouvement</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed animate-fade-in-up animate-delay-200">
            Massothérapie et kinésithérapie à Longueuil. Recommandations et suivis pour un bien être continu.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-300">
            <Button
              onClick={onOpenBooking}
              className="btn-primary pulse-glow flex items-center justify-center gap-2"
              data-testid="hero-cta-booking"
            >
              Prendre Rendez-vous
              <ChevronRight size={20} />
            </Button>
            <button
              onClick={() => document.getElementById("services").scrollIntoView({ behavior: "smooth" })}
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="hero-cta-services"
            >
              Nos Services
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-20 md:py-32 bg-[#F5F3F0]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#E8692D] text-sm font-bold uppercase tracking-widest mb-4">
            Nos expertises
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#686D71]">
            Des soins adaptés à vos besoins
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {SERVICES.map((service) => (
            <div 
              key={service.id}
              className="bg-white rounded-2xl overflow-hidden group shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              data-testid={`service-card-${service.id}`}
            >
              <div className="aspect-video overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-full bg-[#E8692D]/10">
                    <service.icon className="w-6 h-6 text-[#E8692D]" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-wide text-[#686D71]">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {service.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-gray-500">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#E8692D]" />
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="apropos" className="py-20 md:py-32 bg-[#686D71]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#E8692D] text-sm font-bold uppercase tracking-widest mb-4">
              À propos
            </p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6">
              Clinique Mūv
            </h2>
            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              Située à Longueuil, la Clinique Mūv est votre partenaire de confiance depuis 2002, offre une expertise et approche personnalisée pour vous offrir des soins de qualité supérieure.
            </p>
            <p className="text-lg text-gray-300 mb-6 leading-relaxed font-semibold text-[#E8692D]">
              Possibilité de rendez-vous rapide à l'intérieur de 24h.
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              Que vous soyez simplement actif au quotidien ou un athlète de haut niveau, nous avons les solutions adaptées à vos besoins.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <a 
                href="https://www.google.com/search?q=Clinique+Muv+Longueuil+avis"
                target="_blank"
                rel="noopener noreferrer"
                className="service-card text-center hover:border-[#E8692D]/50 cursor-pointer"
                data-testid="google-reviews-link"
              >
                <div className="flex justify-center mb-2">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <div className="text-4xl font-bold text-[#E8692D] mb-1">5.0</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Avis Google</div>
              </a>
              <a 
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="service-card text-center hover:border-[#E8692D]/50 cursor-pointer"
                data-testid="facebook-reviews-link"
              >
                <div className="flex justify-center mb-2">
                  <Facebook className="w-8 h-8 text-[#1877F2]" />
                </div>
                <div className="text-4xl font-bold text-[#E8692D] mb-1">230+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Abonnés Facebook</div>
              </a>
            </div>
          </div>
          <div className="relative flex items-center justify-center">
            <img 
              src={LOGO} 
              alt="Clinique Mūv Logo"
              className="w-full max-w-md h-auto object-contain"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Therapist Section - Marc Dumouchel
const TherapistSection = () => {
  return (
    <section className="py-20 md:py-32 bg-[#F5F3F0]" data-testid="therapist-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[#E8692D] text-sm font-bold uppercase tracking-widest mb-4">
            Votre thérapeute
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-[#686D71] mb-2">
            Marc Dumouchel
          </h2>
          <p className="text-lg text-gray-500">
            Massothérapeute & Kinésithérapeute · <a href="https://rmpq.ca" target="_blank" rel="noopener noreferrer" className="text-[#E8692D] hover:underline">RMPQ #11-8760</a>
          </p>
        </div>

        {/* Bio Text */}
        <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
          <p>
            Marc Dumouchel est un massothérapeute et kinésithérapeute québécois établi à Longueuil. Depuis 2002, il exerce dans le domaine de la santé, a fondé et dirige la Clinique Mūv, qui propose des services de massothérapie, kinésithérapie et réadaptation sportive.
          </p>
          
          <p>
            Au fil des années, il a développé une pratique complète axée sur le bien-être et la thérapie. Il offre des massages suédois, thérapeutiques et de détente, ainsi que des techniques plus spécifiques comme les mobilisations passives et la réflexologie, ce qui témoigne de sa volonté d'adapter ses interventions aux besoins des clients.
          </p>
          
          <p>
            Membre du <a href="https://rmpq.ca" target="_blank" rel="noopener noreferrer" className="text-[#E8692D] hover:underline font-semibold">Réseau des massothérapeutes professionnels du Québec</a>, il est reconnu pour son expertise et bénéficie d'excellentes évaluations de la part de sa clientèle.
          </p>
          
          <p>
            Une carrière qui s'étend sur plus de deux décennies reflète un engagement constant envers une approche holistique de la santé physique, alliant techniques manuelles et réadaptation. Il continue d'exercer avec une réputation solide auprès des patients qui apprécient son professionnalisme et son écoute.
          </p>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ onOpenBooking }) => {
  const mapLinks = {
    googleMaps: `https://www.google.com/maps/dir/?api=1&destination=${CONTACT.addressEncoded}`,
    waze: `https://waze.com/ul?q=${CONTACT.addressEncoded}&navigate=yes`,
    appleMaps: `http://maps.apple.com/?daddr=${CONTACT.addressEncoded}`
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-[#4C5155]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#E8692D] text-sm font-bold uppercase tracking-widest mb-4">
            Contact
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white">
            Nous Trouver
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Therapist Info */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-[#E8692D]/20 shrink-0">
                  <Heart className="w-6 h-6 text-[#E8692D]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">Marc Dumouchel</h3>
                  <p className="text-gray-400">Membre RMPQ #11-8760</p>
                </div>
              </div>
            </div>

            {/* Address Card */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-[#E8692D]/20 shrink-0">
                  <MapPin className="w-6 h-6 text-[#E8692D]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Adresse</h3>
                  <p className="text-gray-300">{CONTACT.address}</p>
                </div>
              </div>
            </div>

            {/* Hours Card */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-[#E8692D]/20 shrink-0">
                  <Clock className="w-6 h-6 text-[#E8692D]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Heures d'ouverture</h3>
                  <p className="text-gray-300">{CONTACT.hours.weekdays}</p>
                  <p className="text-gray-300">{CONTACT.hours.weekend}</p>
                </div>
              </div>
            </div>

            {/* Direction Buttons */}
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Itinéraire</h3>
              <div className="flex flex-wrap gap-3">
                <a
                  href={mapLinks.googleMaps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="direction-btn bg-[#4285F4] text-white"
                  data-testid="direction-google-maps"
                >
                  <Navigation size={18} />
                  Google Maps
                </a>
                <a
                  href={mapLinks.waze}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="direction-btn bg-[#33CCFF] text-white"
                  data-testid="direction-waze"
                >
                  <Navigation size={18} />
                  Waze
                </a>
                <a
                  href={mapLinks.appleMaps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="direction-btn bg-gray-600 text-white"
                  data-testid="direction-apple-maps"
                >
                  <Navigation size={18} />
                  Plans
                </a>
              </div>
            </div>

            {/* CTA */}
            <Button
              onClick={onOpenBooking}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="contact-cta-booking"
            >
              Prendre Rendez-vous
              <ChevronRight size={20} />
            </Button>
          </div>

          {/* Map */}
          <div className="map-container h-[400px] lg:h-full min-h-[400px]">
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2793.993394628965!2d-73.46387668444004!3d45.55053997910191!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc9059f07447917%3A0x627376046755458!2s1887%20Ch%20du%20Tremblay%2C%20Longueuil%2C%20QC%20J4N%201A4!5e0!3m2!1sfr!2sca!4v1625686000000!5m2!1sfr!2sca`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Clinique Mūv Location"
              data-testid="google-map-embed"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 bg-[#4C5155] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img 
              src={LOGO} 
              alt="Clinique Mūv" 
              className="h-12 w-auto"
            />
            <span className="text-gray-400">© 2024 Clinique Mūv. Tous droits réservés.</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-white/10 hover:bg-[#E8692D] text-white transition-colors duration-300"
              data-testid="footer-facebook"
            >
              <Facebook size={20} />
            </a>
            <a
              href={`tel:${CONTACT.phoneClean}`}
              className="p-3 rounded-full bg-white/10 hover:bg-[#E8692D] text-white transition-colors duration-300"
              data-testid="footer-phone"
            >
              <Phone size={20} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Booking Modal
const BookingModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#5A5F63] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold uppercase tracking-wide">
            Prendre Rendez-vous
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <p className="text-gray-300">
            Choisissez votre méthode préférée pour nous contacter:
          </p>
          
          {/* Phone Button */}
          <a
            href={`tel:${CONTACT.phoneClean}`}
            className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#E8692D]/50 transition-colors group"
            data-testid="booking-phone"
          >
            <div className="p-3 rounded-full bg-[#E8692D] group-hover:scale-110 transition-transform">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-white">Par téléphone</p>
              <p className="text-[#E8692D] text-lg font-bold">{CONTACT.phone}</p>
            </div>
          </a>

          {/* Messenger Button */}
          <a
            href={CONTACT.messenger}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#E8692D]/50 transition-colors group"
            data-testid="booking-messenger"
          >
            <div className="p-3 rounded-full bg-[#0084FF] group-hover:scale-110 transition-transform">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-white">Par Messenger</p>
              <p className="text-gray-400">Envoyez-nous un message</p>
            </div>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#686D71]" data-testid="app-container">
      {/* Grain Overlay */}
      <div className="grain-overlay" />
      
      {/* Navigation */}
      <NavBar 
        isScrolled={isScrolled} 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Main Content */}
      <main>
        <HeroSection onOpenBooking={() => setBookingModalOpen(true)} />
        <ServicesSection />
        <AboutSection />
        <TherapistSection />
        <ContactSection onOpenBooking={() => setBookingModalOpen(true)} />
      </main>

      {/* Footer */}
      <Footer />

      {/* Booking Modal */}
      <BookingModal 
        isOpen={bookingModalOpen} 
        onClose={() => setBookingModalOpen(false)} 
      />
    </div>
  );
}

export default App;
